/* eslint-disable no-param-reassign */
import LogRocket from 'logrocket'
import LogrocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer'
import createPlugin from 'logrocket-vuex'
import isElectron from 'is-electron'
import { getUserData } from '@/auth/utils'
import router from '@/router'
import store from '@/store'
import axios from '@axios'
import { getAppVersions } from './utils/app-utils'

const environment = window.env || 'local'
const isLocal = environment === 'local'

let initialized = false

const sanitizeFields = [
  'Authorization',
  'authorization',
  'access_token',
  'refresh_token',
  'cpf',
  'cnpj',
  'document',
  'password',
  'token',
]
const { requestSanitizer, responseSanitizer } = LogrocketFuzzySanitizer.setup(sanitizeFields)

const mutationSanitizer = mutation => {
  if (!initialized) return mutation

  const fakeRequest = {
    body: JSON.stringify(mutation),
    headers: {},
    method: 'POST',
  }
  const { body: sanitized } = responseSanitizer(fakeRequest)
  return sanitized
}

const init = token => {
  if (initialized || !token) return

  const appVersion = JSON.stringify(getAppVersions())

  LogRocket.init(token, {
    release: appVersion,
    network: {
      requestSanitizer: request => {
        const { body: sanitized } = requestSanitizer(request)
        request.body = sanitized

        Object.keys(request.headers)
          .filter(h => h && sanitizeFields.includes(h.toLowerCase()))
          .forEach(h => {
            request.headers[h] = null
          })
        return request
      },
      responseSanitizer,
    },
    dom: {
      // TODO: deixando desabilitado por enqt, até estar melhor validado em produção
      // inputSanitizer: isProd,
    },
  })

  initialized = true
}

function trackEvent(eventName, eventProperties) {
  if (!initialized) return

  try {
    LogRocket.track(eventName, eventProperties)
  } catch (err) {
    console.error(err)
  }
}

async function checkEnabled(
  config,
  userData,
  isTefAction,
  isRewardAction,
  isPayBoxFinishOrderAction,
  isPayBoxSaleAction
) {
  // disabled globally
  const enabled = config?.enabled
  if (!enabled) return enabled

  const users = config?.enabledUserNames || []
  const paths = config?.enabledUrlPaths || []
  const storeIds = config?.enabledStoreIds || []
  const payBoxIds = config?.enabledPayBoxIds || []
  const systemRoles = config?.enabledSystemRoles || []
  const storeTypes = config?.enabledStoreTypes || []

  const tefMonitoring = config?.enabledTefMonitoring || false
  const rewardMonitoring = config?.enabledRewardMonitoring || false
  const payBoxFinishOrderMonitoring = config?.enabledPayBoxFinishOrderMonitoring || false
  const payBoxSaleMonitoring = config?.enabledPayBoxSaleMonitoring || false

  const currentSystemRole = () => {
    if (!isElectron()) return 'ERP'

    if (store.state.pages?.pdv?.payBoxConfiguration?.payBox?.isAtm) {
      return 'ATM'
    }

    return 'PDV'
  }

  const currentStoreType = () => store.state.pages?.pdv?.payBoxConfiguration?.payBox?.store?.type

  const storeOrPayboxEnabled = async () => {
    // all stores/payboxes enabled
    if (!storeIds.length && !payBoxIds.length) return true

    let payboxId = store.state.pages?.pdv?.payBoxConfiguration?.payBox?.id
    let storeId = store.state.pages?.pdv?.payBoxConfiguration?.payBox?.store?.id

    if (!payboxId) {
      const identify = isElectron() ? await window.electronAPI.system.identifyPayboxRead() : null
      if (identify && identify.payboxId) {
        payboxId = identify.payboxId
        storeId = identify?.storeId
      }
    }

    return payBoxIds.includes(payboxId) || storeIds.includes(storeId)
  }

  const systemRoleEnabled = () => {
    // all enabled
    if (!systemRoles.length) return true

    return systemRoles.map(u => u.toLowerCase()).includes(currentSystemRole()?.toLowerCase())
  }

  const storeTypeEnabled = () => {
    // all enabled
    if (!storeTypes.length) return true

    return storeTypes.map(u => u.toLowerCase()).includes(currentStoreType()?.toLowerCase())
  }

  const userEnabled = () => {
    // all users enabled
    if (!users.length) return true

    return users.map(u => u.toLowerCase()).includes(userData.userName?.toLowerCase())
  }

  const pathEnabled = () => {
    // all paths enabled
    if (!paths.length) return true

    return paths.some(p => router.currentRoute.path.startsWith(p))
  }

  const tefMonitoringEnabled = () => {
    if (!tefMonitoring) return true

    return tefMonitoring === isTefAction
  }

  const rewardMonitoringEnabled = () => {
    if (!rewardMonitoring) return true

    return rewardMonitoring === isRewardAction
  }

  const payBoxFinishOrderMonitoringEnabled = () => {
    if (!payBoxFinishOrderMonitoring) return true

    return payBoxFinishOrderMonitoring === isPayBoxFinishOrderAction
  }

  const payBoxSaleMonitoringEnabled = () => {
    if (!payBoxSaleMonitoring) return true

    return payBoxSaleMonitoring === isPayBoxSaleAction
  }

  const checks = {
    storeOrPaybox: await storeOrPayboxEnabled(),
    user: userEnabled(),
    tefMonitoring: tefMonitoringEnabled(),
    path: pathEnabled(),
    reward: rewardMonitoringEnabled(),
    payBoxFinishOrder: payBoxFinishOrderMonitoringEnabled(),
    payBoxSale: payBoxSaleMonitoringEnabled(),
    systemRole: systemRoleEnabled(),
    storeType: storeTypeEnabled(),
  }

  return Object.values(checks).every(Boolean)
}

const vuexPlugin = () => [createPlugin(LogRocket, mutationSanitizer)]

let monitoringLastFetch
const monitoringCacheDuration = isLocal ? 0 : 10 * 60 * 1000 // 10 minutes in milliseconds
let monitoringCacheData = null
let lastIdentifiedUserInfos = null
let lastSessionId = null
let lastSessionUrlCheck
const sessionUrlCheckDuration = isLocal ? 0 : 20 * 60 * 1000 // 20 minutes in milliseconds

const userInfosChanged = newUserInfos => {
  if (!lastIdentifiedUserInfos) return true

  return JSON.stringify(lastIdentifiedUserInfos) !== JSON.stringify(newUserInfos)
}

const fetchMonitoringConfig = async () => {
  const now = Date.now()

  if (!monitoringLastFetch || now - parseInt(monitoringLastFetch, 10) > monitoringCacheDuration) {
    const { data } = await axios.get('/api/parameter/monitoring.config')
    const config = JSON.parse(data?.value || {})
    const logRocketConfig = config?.front?.logRocket

    // Support both single config object and array of configs
    monitoringCacheData = Array.isArray(logRocketConfig) ? logRocketConfig : [logRocketConfig]
    monitoringLastFetch = now
  }

  return monitoringCacheData
}

const findMatchingConfig = async (
  configs,
  userData,
  isTefAction,
  isRewardAction,
  isPayBoxFinishOrderAction,
  isPayBoxSaleAction
) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < configs.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const enabled = await checkEnabled(
      configs[i],
      userData,
      isTefAction,
      isRewardAction,
      isPayBoxFinishOrderAction,
      isPayBoxSaleAction
    )
    if (enabled) {
      return configs[i]
    }
  }
  return null
}

const logSessionUrl = () => {
  const now = Date.now()
  if (!lastSessionUrlCheck || now - parseInt(lastSessionUrlCheck, 10) > sessionUrlCheckDuration) {
    LogRocket.getSessionURL(sessionURL => {
      if (sessionURL) {
        // Compare only base URL without query params
        const currentUrl = new URL(sessionURL)
        const sessionId = `${currentUrl.origin}${currentUrl.pathname}`
        if (sessionId !== lastSessionId) {
          console.debug('LogRocket Session URL:', sessionURL)
          lastSessionId = sessionId
        }
      }
    })
    lastSessionUrlCheck = now
  }
}

const initMonitoring = async ({
  isTefAction,
  isRewardAction,
  isPayBoxFinishOrderAction,
  isPayBoxSaleAction,
}) => {
  try {
    if (isLocal) return

    const userData = getUserData()
    const configs = await fetchMonitoringConfig()

    // Find the first config that meets all criteria
    const selectedConfig = await findMatchingConfig(
      configs,
      userData,
      isTefAction,
      isRewardAction,
      isPayBoxFinishOrderAction,
      isPayBoxSaleAction
    )

    if (!selectedConfig) return

    init(selectedConfig.token)

    const userId = userData.userName
    const userInfos = {
      email: userData.email,
      user: userData.userName,
      environment,
      electron: isElectron().toString(),
      payBoxId: store.state.pages?.pdv?.payBoxConfiguration?.payBox?.id,
      storeId: store.state.pages?.pdv?.payBoxConfiguration?.payBox?.store?.id,
    }

    if (!userInfos.storeId && isElectron()) {
      const identify = await window.electronAPI.system.identifyPayboxRead()
      userInfos.payBoxId = identify?.payboxId
      userInfos.storeId = identify?.storeId
    }

    if (userInfosChanged(userInfos)) {
      LogRocket.identify(userId, userInfos)
      lastIdentifiedUserInfos = userInfos
    }

    logSessionUrl()
  } catch (err) {
    console.error(err)
  }
}

export { initMonitoring, trackEvent, vuexPlugin }
